import { useEffect, useRef, useState, type ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Strip markdown Haiku might still emit despite the prompt, then split into
// numbered-step rows + math chips. Falls back to plain paragraph if there's no
// numbered structure (short conceptual answers).
function renderBuddyText(raw: string): ReactNode {
  const cleaned = raw
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/(?<!\w)_(.+?)_(?!\w)/g, '$1')
    .replace(/^#+\s+/gm, '')
    .replace(/^\s*[-•]\s+/gm, '')
    .trim();

  const lines = cleaned
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  const numberedRe = /^(\d+)[.)]\s*(.+)$/;
  const answerRe = /^(?:answer|final answer|final)\s*[:\-]\s*(.+)$/i;

  const preamble: string[] = [];
  const steps: { num: string; text: string }[] = [];
  const trailing: string[] = [];
  let answer: string | null = null;

  for (const line of lines) {
    const numMatch = line.match(numberedRe);
    const ansMatch = line.match(answerRe);
    if (numMatch) {
      steps.push({ num: numMatch[1], text: numMatch[2] });
    } else if (ansMatch) {
      answer = ansMatch[1].trim();
    } else if (steps.length === 0) {
      preamble.push(line);
    } else {
      trailing.push(line);
    }
  }

  if (steps.length === 0) {
    return <div className="whitespace-pre-wrap">{renderInlineMath(cleaned)}</div>;
  }

  return (
    <div className="space-y-0.5">
      {preamble.length > 0 && (
        <div className="mb-2 font-semibold">{renderInlineMath(preamble.join(' '))}</div>
      )}
      {steps.map((step, i) => (
        <div key={i} className="flex gap-2.5 items-start py-1">
          <div className="w-[22px] h-[22px] rounded-full bg-coral/15 text-coral font-display font-black text-[11px] flex items-center justify-center shrink-0 mt-0.5">
            {step.num}
          </div>
          <div className="flex-1 leading-snug">{renderInlineMath(step.text)}</div>
        </div>
      ))}
      {trailing.length > 0 && (
        <div className="mt-2 leading-snug">{renderInlineMath(trailing.join(' '))}</div>
      )}
      {answer && (
        <div className="mt-2 bg-sky text-white px-3 py-2 rounded-xl font-display font-black text-sm flex items-center gap-2">
          <span>✓</span>
          <span>Answer: {answer}</span>
        </div>
      )}
    </div>
  );
}

// Wrap math expressions like "1 × 3 = 3" or "3 + 3 + 6 + 6 + 2 + 2 = 22" in a
// monospace chip. Result after "=" gets sky color. Non-math prose passes through.
function renderInlineMath(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const re = /(\d+(?:\.\d+)?(?:\s*[×÷*/+\-−]\s*\d+(?:\.\d+)?)+(?:\s*=\s*-?\d+(?:\.\d+)?)?)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const expr = m[0];
    const eq = expr.lastIndexOf('=');
    if (eq > -1) {
      nodes.push(
        <span
          key={key++}
          className="font-mono font-bold bg-cream px-1.5 py-0.5 rounded-md whitespace-nowrap"
        >
          {expr.slice(0, eq)}={' '}
          <span className="text-sky font-extrabold">{expr.slice(eq + 1).trim()}</span>
        </span>
      );
    } else {
      nodes.push(
        <span
          key={key++}
          className="font-mono font-bold bg-cream px-1.5 py-0.5 rounded-md whitespace-nowrap"
        >
          {expr}
        </span>
      );
    }
    last = m.index + expr.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

export interface HintContext {
  problem_text?: string;
  final_answer?: string;
  chapter?: string | null;
  topic?: string;
  scaffold_summary?: string;
}

interface Props {
  context: HintContext;
  deviceId: string;
}

interface Turn {
  role: 'user' | 'buddy';
  text: string;
}

// "Ask Professor Buddy" — collapsed pill that floats at the top of the Solve
// screen. Tap the pill to expand into a full chat panel. Grace's question
// history persists while she's on the same problem.
export default function AskBuddy({ context, deviceId }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [question, setQuestion] = useState('');
  const [history, setHistory] = useState<Turn[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [remaining, setRemaining] = useState<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll conversation to newest.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [history, loading]);

  // Focus the input when the panel opens.
  useEffect(() => {
    if (expanded) setTimeout(() => inputRef.current?.focus(), 150);
  }, [expanded]);

  const ask = async () => {
    const q = question.trim();
    if (!q || loading) return;

    const newUserTurn: Turn = { role: 'user', text: q };
    const nextHistory = [...history, newUserTurn];
    setHistory(nextHistory);
    setQuestion('');
    setLoading(true);
    setError(null);

    try {
      const res = await fetch('/api/hint', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-device-id': deviceId,
        },
        body: JSON.stringify({
          question: q,
          problem_text: context.problem_text,
          final_answer: context.final_answer,
          chapter: context.chapter ?? undefined,
          topic: context.topic,
          scaffold_summary: context.scaffold_summary,
          history: history.slice(-6).map((t) => ({ role: t.role, text: t.text })),
        }),
      });
      const data = (await res.json()) as { hint?: string; error?: string; remaining?: number };
      if (!res.ok || data.error) {
        setError(
          data.error === 'hint_limit_reached'
            ? "We've hit today's limit for questions. Try again tomorrow!"
            : "Sorry — Buddy couldn't answer that one. Try asking a different way."
        );
      } else if (data.hint) {
        setHistory([...nextHistory, { role: 'buddy', text: data.hint }]);
        if (typeof data.remaining === 'number') setRemaining(data.remaining);
      }
    } catch {
      setError('Network hiccup. Try again in a moment.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Collapsed pill — always shown when not expanded */}
      <AnimatePresence initial={false}>
        {!expanded && (
          <motion.button
            key="pill"
            onClick={() => setExpanded(true)}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            className="w-full bg-white rounded-2xl shadow-pop border-2 border-violet/40 px-3 py-2.5 flex items-center gap-3 text-left hover:border-violet/70 active:scale-[0.99] transition"
            aria-label="Ask Professor Buddy"
          >
            <div className="w-8 h-8 rounded-full overflow-hidden shrink-0 ring-2 ring-white">
              <img src="/mascot.jpg" alt="" className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-black text-sm leading-tight">
                Ask Professor Buddy
              </div>
              <div className="text-[11px] text-ink/60 truncate">
                {history.length > 0
                  ? `${history.length / 2 < 1 ? 1 : Math.ceil(history.length / 2)} question${history.length >= 4 ? 's' : ''} so far — tap to continue`
                  : 'Tap to ask a question about this problem'}
              </div>
            </div>
            <div className="text-violet font-bold text-xl leading-none pr-1">＋</div>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Expanded panel */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            key="panel"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            className="w-full bg-white rounded-2xl shadow-pop border-2 border-violet/40 p-4 space-y-3"
          >
            {/* Header with close button */}
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full overflow-hidden shrink-0 ring-2 ring-white">
                <img src="/mascot.jpg" alt="" className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-black text-base leading-tight">
                  Ask Professor Buddy
                </div>
                <div className="text-[11px] text-ink/60 truncate">
                  {context.topic
                    ? `${context.topic}${context.chapter ? ` · Section ${context.chapter}` : ''}`
                    : 'Any question about this problem'}
                </div>
              </div>
              <button
                onClick={() => setExpanded(false)}
                className="shrink-0 w-8 h-8 rounded-full bg-ink/5 hover:bg-ink/10 text-ink/60 font-bold transition"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            {/* Conversation */}
            {history.length > 0 && (
              <div
                ref={scrollRef}
                className="bg-cream rounded-xl p-3 max-h-64 overflow-y-auto space-y-2.5"
              >
                <AnimatePresence initial={false}>
                  {history.map((turn, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex gap-2 ${turn.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      {turn.role === 'buddy' && (
                        <div className="w-6 h-6 rounded-full overflow-hidden shrink-0 mt-0.5">
                          <img src="/mascot.jpg" alt="" className="w-full h-full object-cover" />
                        </div>
                      )}
                      <div
                        className={`px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                          turn.role === 'user'
                            ? 'bg-coral text-white rounded-br-sm max-w-[80%]'
                            : 'bg-white text-ink rounded-bl-sm shadow-sm max-w-[92%]'
                        }`}
                      >
                        {turn.role === 'buddy' ? renderBuddyText(turn.text) : turn.text}
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
                {loading && (
                  <div className="flex gap-2 justify-start">
                    <div className="w-6 h-6 rounded-full overflow-hidden shrink-0 mt-0.5">
                      <img src="/mascot.jpg" alt="" className="w-full h-full object-cover" />
                    </div>
                    <div className="bg-white px-3 py-2 rounded-2xl rounded-bl-sm shadow-sm">
                      <div className="flex gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-ink/40 animate-pulse" />
                        <span className="w-1.5 h-1.5 rounded-full bg-ink/40 animate-pulse" style={{ animationDelay: '150ms' }} />
                        <span className="w-1.5 h-1.5 rounded-full bg-ink/40 animate-pulse" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {error && (
              <div className="bg-sun/20 text-ink text-sm rounded-xl px-3 py-2">{error}</div>
            )}

            {/* Input */}
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    ask();
                  }
                  if (e.key === 'Escape') setExpanded(false);
                }}
                placeholder={
                  history.length === 0
                    ? 'Type a question about this problem…'
                    : 'Ask a follow-up…'
                }
                maxLength={500}
                disabled={loading}
                className="flex-1 px-4 py-2.5 rounded-2xl border-2 border-ink/10 bg-white text-sm outline-none focus:border-violet/60 disabled:opacity-50"
              />
              <button
                onClick={ask}
                disabled={loading || !question.trim()}
                className="btn-primary disabled:opacity-50"
                aria-label="Ask"
              >
                Ask →
              </button>
            </div>

            {remaining !== null && remaining < 10 && (
              <div className="text-xs text-ink/50 text-right">
                {remaining} question{remaining === 1 ? '' : 's'} left today
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
